import { RoomField } from './field';

export enum LevelType {
  UPPER = 'Upper',
  MAIN = 'Main',
  LOWER = 'Lower',
  UNSPECIFIED = 'Unspecified',
}

export interface Room {
  [RoomField.ROOM_KEY]: string;
  [RoomField.ROOM_TYPE]: string;
  [RoomField.ROOM_ITEM_NUMBER]: number;
  [RoomField.ROOM_LENGTH]: number;
  [RoomField.ROOM_WIDTH]: number;
  [RoomField.ROOM_LEVEL]: string;
  [RoomField.ROOM_DISPLAY_ORDER]: number;
  [RoomField.ROOM_FEATURES]: string[];
  [RoomField.ROOM_FLOORING]: string[];
  [RoomField.ROOM_DESCRIPTION]?: string | null;
  ROOMLIS_KEY?: string;
}

export interface RoomGroupType {
  label: string;
  type: string;
  groupWith?: string[];
  count?: number;
}

export interface LevelCounts {
  bedrooms: number;
  fullBaths: number;
  halfBaths: number;
  laundry: number;
  hasRoom: boolean;
}

export interface RoomSummary {
  levels: number;
  totalFullBaths: number;
  totalHalfBaths: number;
  totalBedrooms: number;
  totalRooms: number;
  totalBathrooms: number;
  totalLaundry: number;
  storyList: string[];
  upperLevels: { [key: number]: LevelCounts };
  lowerLevels: { [key: number]: LevelCounts };
  mainLevel: LevelCounts;
  // Aggregate counts across all levels of each type
  fullBathsLower: number;
  fullBathsMain: number;
  fullBathsUpper: number;
  halfBathsLower: number;
  halfBathsMain: number;
  halfBathsUpper: number;
  laundryLower: number;
  laundryMain: number;
  laundryUpper: number;
}

const createEmptyLevelCounts = (hasRoom: boolean = false): LevelCounts => ({
  bedrooms: 0,
  fullBaths: 0,
  halfBaths: 0,
  laundry: 0,
  hasRoom,
});

const getRoomType = (roomType: string) => {
  const type = roomType.toLowerCase();
  return {
    // Handle both full names ("Bedroom") and abbreviations ("BED")
    isBedroom: type.includes('bedroom') || type === 'bed',
    isFullBath:
      (type.includes('bathroom') ||
        type.includes('full bath') ||
        type === 'bath') &&
      !type.includes('half'),
    isHalfBath: type.includes('half bath') || type.includes('half'),
    isLaundry: type.includes('laundry'),
  };
};

export const normalizeLevel = (
  level: string,
): { type: LevelType; number?: number } => {
  const normalized = level?.toLowerCase() || '';
  if (
    normalized === LevelType.UNSPECIFIED.toLowerCase() ||
    normalized === LevelType.MAIN.toLowerCase()
  ) {
    return { type: LevelType.MAIN };
  }

  const match = normalized.match(/(upper|lower)\s*(\d+)/);
  if (!match) return { type: LevelType.MAIN };

  const typeString = match[1]; // "upper" or "lower"
  let determinedType: LevelType;

  if (typeString === 'upper') {
    determinedType = LevelType.UPPER;
  } else if (typeString === 'lower') {
    determinedType = LevelType.LOWER;
  } else {
    // This case should not be reached with the current regex
    determinedType = LevelType.MAIN; // Fallback
  }

  return {
    type: determinedType,
    number: parseInt(match[2]),
  };
};

export const calculateRoomSummary = (
  rooms: Room[],
  emptyLevels: string[],
): RoomSummary => {
  const summary: RoomSummary = {
    levels: 0,
    totalFullBaths: 0,
    totalHalfBaths: 0,
    totalBedrooms: 0,
    totalRooms: 0,
    totalBathrooms: 0,
    totalLaundry: 0,
    storyList: ['Main'], // Initialize with Main by default
    upperLevels: {},
    lowerLevels: {},
    mainLevel: createEmptyLevelCounts(true),
    // Aggregate counts across all levels of each type
    fullBathsLower: 0,
    fullBathsMain: 0,
    fullBathsUpper: 0,
    halfBathsLower: 0,
    halfBathsMain: 0,
    halfBathsUpper: 0,
    laundryLower: 0,
    laundryMain: 0,
    laundryUpper: 0,
  };

  // Initialize all levels with zero counts
  for (let i = 1; i <= 6; i++) {
    summary.upperLevels[i] = createEmptyLevelCounts();
    summary.lowerLevels[i] = createEmptyLevelCounts();
  }

  // Track unique levels for story list
  const uniqueUpperLevels = new Set<string>();
  const uniqueLowerLevels = new Set<string>();

  // Set Total Rooms
  summary.totalRooms = rooms.length;

  rooms.forEach((room) => {
    const { isBedroom, isFullBath, isHalfBath, isLaundry } = getRoomType(
      room.ROOM_TYPE,
    );
    const rawLevel = room.ROOM_LEVEL || '';
    const level = normalizeLevel(rawLevel);

    if (
      rawLevel &&
      rawLevel.toLowerCase() !== LevelType.UNSPECIFIED.toLowerCase()
    ) {
      if (level.type === LevelType.UPPER) {
        uniqueUpperLevels.add(rawLevel);
      } else if (level.type === LevelType.LOWER) {
        uniqueLowerLevels.add(rawLevel);
      }
    }

    if (isBedroom) summary.totalBedrooms++;
    if (isFullBath) summary.totalFullBaths++;
    if (isHalfBath) summary.totalHalfBaths++;
    if (isLaundry) summary.totalLaundry++;
    summary.totalBathrooms = summary.totalFullBaths + summary.totalHalfBaths;

    // Add to appropriate level counts
    if (level.type === LevelType.MAIN) {
      if (isBedroom) summary.mainLevel.bedrooms++;
      if (isFullBath) summary.mainLevel.fullBaths++;
      if (isHalfBath) summary.mainLevel.halfBaths++;
      if (isLaundry) summary.mainLevel.laundry++;
    } else if (level.number) {
      const counts =
        level.type === LevelType.UPPER
          ? summary.upperLevels[level.number]
          : summary.lowerLevels[level.number];

      // there is a room at this level, lets update the room counts
      counts.hasRoom = true;

      if (isBedroom) counts.bedrooms++;
      if (isFullBath) counts.fullBaths++;
      if (isHalfBath) counts.halfBaths++;
      if (isLaundry) counts.laundry++;
    }
  });

  emptyLevels.forEach((rawLevel) => {
    const level = normalizeLevel(rawLevel);

    if (
      rawLevel &&
      rawLevel.toLowerCase() !== LevelType.UNSPECIFIED.toLowerCase()
    ) {
      if (level.type === LevelType.UPPER) {
        uniqueUpperLevels.add(rawLevel);
      } else if (level.type === LevelType.LOWER) {
        uniqueLowerLevels.add(rawLevel);
      }
    }
  });

  // Calculate total levels (Main level is always counted)
  summary.levels = 1 + uniqueUpperLevels.size + uniqueLowerLevels.size;

  // Create sorted story list with Main always in the middle
  const upperLevels = Array.from(uniqueUpperLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number ?? 0;
    const numB = normalizeLevel(b).number ?? 0;
    return numA - numB;
  });

  const lowerLevels = Array.from(uniqueLowerLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number ?? 0;
    const numB = normalizeLevel(b).number ?? 0;
    return numA - numB;
  });

  summary.storyList = [...upperLevels, 'Main', ...lowerLevels];

  // Calculate aggregate counts across all levels of each type
  summary.fullBathsLower = Object.values(summary.lowerLevels).reduce(
    (sum, l) => sum + l.fullBaths,
    0,
  );
  summary.fullBathsUpper = Object.values(summary.upperLevels).reduce(
    (sum, l) => sum + l.fullBaths,
    0,
  );
  summary.fullBathsMain = summary.mainLevel.fullBaths;

  summary.halfBathsLower = Object.values(summary.lowerLevels).reduce(
    (sum, l) => sum + l.halfBaths,
    0,
  );
  summary.halfBathsUpper = Object.values(summary.upperLevels).reduce(
    (sum, l) => sum + l.halfBaths,
    0,
  );
  summary.halfBathsMain = summary.mainLevel.halfBaths;

  summary.laundryLower = Object.values(summary.lowerLevels).reduce(
    (sum, l) => sum + l.laundry,
    0,
  );
  summary.laundryUpper = Object.values(summary.upperLevels).reduce(
    (sum, l) => sum + l.laundry,
    0,
  );
  summary.laundryMain = summary.mainLevel.laundry;

  return summary;
};
